import React, { useRef } from 'react';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';
import { twMerge } from 'tailwind-merge';
import { LiliError } from '../LiliError';
import { Spinner, SpinnerColor } from '../Spinner';
import { Text, useFontClass } from '../../tokens';

interface LiliFloatingLabelProps {
  children: React.ReactNode;
  errorMessage?: string;
  label?: string;
  onClear?: () => void;
  inilineChild?: React.ReactNode;
  AdditionalIcon?: React.ComponentType;
  onAdditionalIconClick?: () => void;
  accessoryItemComponent?: React.ReactNode;
  showClear?: boolean;
  disabled?: boolean;
  id: string;
  isLoading?: boolean;
  hint?: string;
  hideError?: boolean;
  'data-testid'?: string;
}

export const LiliFloatingLabel = (props: LiliFloatingLabelProps) => {
  const {
    children,
    errorMessage,
    label,
    onClear,
    inilineChild,
    AdditionalIcon,
    onAdditionalIconClick,
    accessoryItemComponent,
    showClear,
    disabled,
    isLoading,
    id,
    hint,
    hideError,
    'data-testid': testId,
  } = props;

  const elBtnWrapper = useRef<HTMLDivElement | null>(null);

  const handleInputWrapperClick = () => {
    const refElement: HTMLInputElement | null | undefined =
      elBtnWrapper.current?.querySelector('input');
    if (refElement) {
      refElement.focus();
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
  };

  const handleAdditionalIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onAdditionalIconClick) {
      onAdditionalIconClick();
    }
  };

  const { fontClass } = useFontClass();

  // some of the styling exists in index.css under #LiliFloatinginputWrapper
  const floatingLabelStyle = twMerge(
    `pointer-events-none absolute transform ease-out duration-200 top-1/2 -translate-y-1/2 ${fontClass} text-[16px]`,
    errorMessage && !disabled ? '!text-text-negative-red' : 'text-text-main-tertiary'
  );

  const containerStyle = twMerge(
    'relative w-full h-[54px] px-[16px] rounded-[8px] border border-outline-primary-bw-04',
    disabled ? 'bg-surface-primary-bw-02 cursor-auto border-none text-text-main-secondary' : '',
    errorMessage && '!border-outline-negative-primary'
  );
  const dataTestId = testId ?? id;

  const floatingInputWrapperStyle = twMerge(
    'w-[calc(100%-32px)]',
    label ? 'absolute bottom-[7px]' : 'h-full flex items-center'
  );
  return (
    <div
      className={`${isLoading ? 'pointer-events-none' : ''} flex flex-col ${hideError ? '' : 'min-h-[80px]'}`}
      id={`${id}-floating-label`}
      data-testid={`${dataTestId}-floating-label-wrapper`}>
      <div
        ref={elBtnWrapper}
        id='LiliFloatingWrapper'
        onClick={handleInputWrapperClick}
        className={containerStyle}>
        <div id='LiliFloatinginputWrapper' className={floatingInputWrapperStyle}>
          {children}
        </div>
        <p
          id='floatingLabel'
          data-testid={`${dataTestId}-floating-label`}
          className={floatingLabelStyle}>
          {label}
        </p>
        {hint && (
          <Text.H10
            id='floatingLabelHint'
            data-testid={`${dataTestId}-floating-label-hint`}
            className={twMerge(
              'mt-[22px] text-text-main-tertiary',
              hint ? 'visible' : 'invisible'
            )}>
            {hint}
          </Text.H10>
        )}
        {!isLoading && onClear && showClear && !disabled && (
          <button
            id='ClearInput'
            data-testid={`${dataTestId}-clear-input`}
            className='w-fit hidden absolute top-1/2 z-10 -translate-y-1/2 right-[16px]'
            type='button'
            onClick={handleClear}>
            <ClearIcon className='w-[24px]' />
          </button>
        )}
        {!isLoading && AdditionalIcon && (
          <button
            id={`AdditionalIcon-${label ?? id}`}
            data-testid={`${dataTestId}-additional-icon`}
            className='w-fit absolute top-1/2 z-10 -translate-y-1/2 right-[16px]'
            type='button'
            onClick={handleAdditionalIconClick}>
            <AdditionalIcon />
          </button>
        )}
        {isLoading && (
          <Spinner
            containerClassName='absolute ml-auto w-fit top-1/2 z-10 -translate-y-1/2'
            spinnerColor={SpinnerColor.Blue}
            height='1rem'
            width='1rem'
          />
        )}
        {accessoryItemComponent && accessoryItemComponent}
      </div>
      {!hideError && (
        <LiliError
          id={`${id}-error-container`}
          data-testid={`${dataTestId}-error-container`}
          errorMessage={errorMessage}
          inlineChild={inilineChild}
        />
      )}
    </div>
  );
};
